import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError, takeUntil, tap } from 'rxjs';
import { environment } from '../../environment/environment';
import { AUTH_TOKEN_STORAGE_KEY, Authentification } from './authentification';

function estEndpointAuthentification(url: string): boolean {
  return url.includes('/login/check') || url.includes('/token/refresh');
}

function estApiTecsol(url: string): boolean {
  return url.startsWith(environment.apiUrl);
}

/** Construit les headers HTTP de base (Authorization + JWT si disponible). */
function headersAvecToken(baseHeaders: Record<string, string>): Record<string, string> {
  const headers = { ...baseHeaders };
  if (typeof localStorage !== 'undefined') {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (token) headers['X-JWT-Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!estApiTecsol(req.url)) {
    return next(req);
  }

  const auth = inject(Authentification);
  const sessionVersion = auth.sessionVersion;

  const baseHeaders: Record<string, string> = {};
  if (environment.basicAuthorization) {
    baseHeaders['Authorization'] = environment.basicAuthorization;
  }

  // Les endpoints d'authentification n'ont pas besoin du JWT
  if (estEndpointAuthentification(req.url)) {
    return next(req.clone({ setHeaders: baseHeaders }));
  }

  if (typeof localStorage === 'undefined') {
    return next(req.clone({ setHeaders: baseHeaders }));
  }

  return next(req.clone({ setHeaders: headersAvecToken(baseHeaders) })).pipe(
    catchError((error: HttpErrorResponse) => {
      if (sessionVersion !== auth.sessionVersion) return throwError(() => error);
      // Ne tenter le refresh que sur les 401 des endpoints non-auth
      // (évite les boucles infinies sur /token/refresh lui-même)
      if (error.status !== 401 || estEndpointAuthentification(req.url)) {
        return throwError(() => error);
      }

      // Token expiré : rafraîchir puis rejouer la requête avec le nouveau token
      return from(auth.forceTokenRefresh()).pipe(
        switchMap((refreshed) => {
          if (sessionVersion !== auth.sessionVersion) return throwError(() => error);
          if (!refreshed) {
            // Refresh impossible → déconnexion
            auth.logOut();
            return throwError(() => error);
          }
          // Rejouer la requête originale avec le token fraîchement obtenu
          return next(req.clone({ setHeaders: headersAvecToken(baseHeaders) }));
        }),
      );
    }),
    tap(() => {
      if (sessionVersion !== auth.sessionVersion) throw new Error('Session obsolète');
    }),
    takeUntil(auth.sessionChanges),
  );
};
